import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Redirect old/common URLs to their ShedX equivalents
    const redirectMap = {
      '/sheds-for-sale': '/sheds',
      '/shed-inventory': '/sheds',
      '/storage-sheds': '/sheds',
      '/buildings': '/sheds',
      '/products': '/sheds',
      '/shop': '/sheds',
      '/catalog': '/sheds',
      '/about-us': '/about',
      '/our-team': '/about',
      '/contact-us': '/contact',
      '/hours-directions': '/contact',
      '/location': '/contact',
      '/locations': '/dealers',
      '/find-dealer': '/dealers',
      '/find-a-dealer': '/dealers',
      '/dealer-locator': '/dealers',
      '/financing': '/finance-application',
      '/finance': '/finance-application',
      '/rent-to-own': '/finance-application',
      '/rto': '/finance-application',
      '/warranty-info': '/warranty',
      '/guarantees': '/warranty',
      '/wp-admin': '/admin',
      '/dashboard': '/admin',
      '/dealer-login': '/dealer/login',
      '/login': '/dealer/login',
    };

    const currentPath = location.pathname.toLowerCase();

    // Direct match
    if (redirectMap[currentPath]) {
      navigate(redirectMap[currentPath], { replace: true });
      return;
    }

    // Check for old PHP/HTML extensions
    const pathWithoutExtension = currentPath.replace(/\.(php|html|htm|aspx)$/, '');
    if (redirectMap[pathWithoutExtension]) {
      navigate(redirectMap[pathWithoutExtension], { replace: true });
      return;
    }

    // Log 404s for tracking
    console.log('404 - Page not found:', currentPath);
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: '404 Not Found',
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center px-6"
      >
        <h1 className="text-9xl font-bold text-emerald-600 mb-4">404</h1>
        <h2 className="text-3xl font-semibold text-gray-800 mb-4">
          Page Not Found
        </h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          The page you're looking for doesn't exist or has been moved. 
          Let's get you back on track!
        </p>
        
        <div className="space-y-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
          >
            Go to Homepage
          </motion.button>
          
          <div className="flex justify-center space-x-4 mt-6">
            <button
              onClick={() => navigate('/sheds')}
              className="text-emerald-600 hover:text-emerald-800 underline"
            >
              Browse Sheds
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="text-emerald-600 hover:text-emerald-800 underline"
            >
              Contact Us
            </button>
          </div>
        </div>

        {/* Helpful suggestions */}
        <div className="mt-12 p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Were you looking for one of these?
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <a href="/sheds" className="text-emerald-600 hover:underline">
              Shed Inventory
            </a>
            <a href="/dealers" className="text-emerald-600 hover:underline">
              Find a Dealer
            </a>
            <a href="/finance-application" className="text-emerald-600 hover:underline">
              Financing Options
            </a>
            <a href="/warranty" className="text-emerald-600 hover:underline">
              Warranty Info
            </a>
            <a href="/about" className="text-emerald-600 hover:underline">
              About ShedX
            </a>
            <a href="/contact" className="text-emerald-600 hover:underline">
              Contact Us
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default NotFound;
